import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw, Copy, Check } from 'lucide-react';
import { DetailedError, getErrorIcon } from '../utils/errorHandler';

interface DetailedErrorDisplayProps {
  error: DetailedError;
  onRetry?: () => void;
  className?: string;
  retryLabel?: string;
  isRetrying?: boolean;
}

const DetailedErrorDisplay: React.FC<DetailedErrorDisplayProps> = ({
  error,
  onRetry,
  className = '',
  retryLabel = '重試',
  isRetrying = false
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const getTypeColor = (type: DetailedError['type']) => {
    switch (type) {
      case 'network': return 'border-blue-500 bg-blue-50 dark:bg-blue-900/20';
      case 'auth': return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20';
      case 'cors': return 'border-purple-500 bg-purple-50 dark:bg-purple-900/20';
      case 'server': return 'border-red-500 bg-red-50 dark:bg-red-900/20';
      default: return 'border-gray-500 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getTextColor = (type: DetailedError['type']) => {
    switch (type) {
      case 'network': return 'text-blue-800 dark:text-blue-200';
      case 'auth': return 'text-yellow-800 dark:text-yellow-200';
      case 'cors': return 'text-purple-800 dark:text-purple-200';
      case 'server': return 'text-red-800 dark:text-red-200';
      default: return 'text-gray-800 dark:text-gray-200';
    }
  };

  const copyErrorDetails = async () => {
    const errorInfo = `
錯誤類型: ${error.type}
錯誤標題: ${error.title}
錯誤訊息: ${error.message}
詳細資訊: ${error.details || '無'}
時間: ${new Date().toLocaleString('zh-TW')}
URL: ${window.location.href}
用戶代理: ${navigator.userAgent}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorInfo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy error details:', err);
    }
  };

  return (
    <div className={`border-l-4 rounded-lg p-6 ${getTypeColor(error.type)} ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="text-2xl">{getErrorIcon(error.type)}</div>
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${getTextColor(error.type)}`}>
              {error.title}
            </h3>
            <p className={`mt-1 ${getTextColor(error.type)} opacity-90`}>
              {error.message}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2 ml-4">
          {onRetry && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
              <span>{isRetrying ? '重試中...' : retryLabel}</span>
            </button>
          )}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className={`flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${getTextColor(error.type)}`}
          >
            <span className="text-sm">詳細資訊</span>
            {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {showDetails && (
        <div className="mt-4 pt-4 border-t border-current/20">
          {error.details && (
            <div className="mb-4">
              <h4 className={`font-medium ${getTextColor(error.type)} mb-2`}>錯誤詳細資訊:</h4>
              <div className={`bg-black/5 dark:bg-white/5 rounded-lg p-3 font-mono text-sm ${getTextColor(error.type)}`}>
                {error.details}
              </div>
            </div>
          )}

          {error.suggestions && error.suggestions.length > 0 && (
            <div className="mb-4">
              <h4 className={`font-medium ${getTextColor(error.type)} mb-2`}>建議解決方案:</h4>
              <ul className={`space-y-1 ${getTextColor(error.type)} opacity-90`}>
                {error.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-current/60 mt-1">•</span>
                    <span className="text-sm">{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className={`text-xs ${getTextColor(error.type)} opacity-60`}>
              發生時間: {new Date().toLocaleString('zh-TW')}
            </div>
            <button
              onClick={copyErrorDetails}
              className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-sm hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${getTextColor(error.type)}`}
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3" />
                  <span>已複製</span>
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3" />
                  <span>複製錯誤資訊</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedErrorDisplay;